import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Space, Button, Input, Select, DatePicker, Row, Col, Typography,
  Tooltip, message, Popconfirm, Badge, Empty, Collapse,
} from 'antd';
import {
  PhoneOutlined, WhatsAppOutlined, EyeOutlined, CheckOutlined, DeleteOutlined,
  CalendarOutlined, SearchOutlined, ReloadOutlined, DownloadOutlined, PlusOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchFollowups, completeFollowup, deleteFollowup } from '../services/callService';
import { fetchSettings } from '../services/settingsService';
import { TYPE_COLORS, STATUS_COLORS, PRIORITY_COLORS, BRAND } from './callConstants';
import FollowupDetailDrawer from './FollowupDetailDrawer';

const { Title, Text } = Typography;
const fmtT = (d) => (d ? dayjs(d).format('DD MMM, hh:mm A') : '—');

const VIEW_META = {
  today: { title: "Today's Follow-ups", subtitle: 'Calls scheduled for today' },
  upcoming: { title: 'Upcoming Follow-ups', subtitle: 'All future follow-ups' },
  overdue: { title: 'Overdue Follow-ups', subtitle: 'Missed their scheduled time' },
  completed: { title: 'Completed Follow-ups', subtitle: 'History of completed follow-ups' },
  missed: { title: 'Missed Follow-ups', subtitle: 'Marked as missed' },
  history: { title: 'Call History', subtitle: 'Every logged call' },
};

const FollowupList = ({ view = 'today' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchParams] = useSearchParams();
  // Seed filters/search from the URL so dashboard cards can deep-link here.
  const paramFilters = {};
  ['FollowupType', 'Priority', 'ExecutiveName', 'City', 'date', 'Status'].forEach((k) => {
    const v = searchParams.get(k);
    if (v) paramFilters[k] = v;
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState(paramFilters);
  const [settings, setSettings] = useState({});
  const [detail, setDetail] = useState(null);

  // Re-apply when the query string changes (e.g. clicking another card)
  useEffect(() => {
    const next = {};
    ['FollowupType', 'Priority', 'ExecutiveName', 'City', 'date', 'Status'].forEach((k) => {
      const v = searchParams.get(k);
      if (v) next[k] = v;
    });
    setFilters(next);
    setSearch(searchParams.get('search') || '');
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const meta = VIEW_META[view] || VIEW_META.today;
  const grouped = view === 'upcoming';

  useEffect(() => { (async () => { try { setSettings(await fetchSettings()); } catch { /* ignore */ } })(); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFollowups({
        view, page, pageSize: grouped ? 500 : pageSize, search, ...filters,
      });
      setData(res.data); setTotal(res.total);
    } catch { message.error('Failed to load follow-ups'); }
    finally { setLoading(false); }
  }, [view, page, pageSize, search, filters, grouped]);

  useEffect(() => { setPage(1); }, [view]);
  useEffect(() => { load(); }, [load]);

  const handleComplete = async (r) => {
    try { await completeFollowup(r.FollowupID); message.success('Completed'); load(); }
    catch { message.error('Failed'); }
  };
  const handleDelete = async (r) => {
    try { await deleteFollowup(r.FollowupID); message.success('Deleted'); load(); }
    catch { message.error('Failed'); }
  };

  const setFilter = (k, v) => { setPage(1); setFilters(p => { const n = { ...p }; if (v == null || v === '') delete n[k]; else n[k] = v; return n; }); };

  const exportCsv = async () => {
    const res = await fetchFollowups({ view, page: 1, pageSize: 10000, search, ...filters });
    const rows = res.data || [];
    if (!rows.length) { message.info('Nothing to export'); return; }
    const cols = ['CustomerName', 'CustomerMobile', 'City', 'Priority', 'FollowupType', 'ExecutiveName',
      'Status', 'ScheduledAt', 'CallAt', 'CallStatus', 'Outcome', 'Duration', 'Notes'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `followups_${view}_${dayjs().format('YYYYMMDD_HHmm')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const timeCol = view === 'history' ? 'CallAt' : (view === 'completed' ? 'CompletedAt' : 'ScheduledAt');

  const baseColumns = [
    {
      title: view === 'history' ? 'Date/Time' : 'Time', dataIndex: timeCol, key: 'time', width: 150,
      render: (d, r) => {
        const val = fmtT(d || r.ScheduledAt || r.CallAt);
        if (view === 'overdue' && r.ScheduledAt) {
          const days = dayjs().diff(dayjs(r.ScheduledAt), 'day');
          return <span><Text type="danger">{val}</Text><br /><Text type="danger" style={{ fontSize: 11 }}>{days > 0 ? `${days}d overdue` : 'overdue'}</Text></span>;
        }
        return val;
      },
    },
    {
      title: 'Customer', dataIndex: 'CustomerName', key: 'CustomerName', width: 180,
      render: (t, r) => (
        <div>
          <Text strong>{t || '—'}</Text>
          {r.EntityType && <Tag style={{ marginLeft: 6, fontSize: 10 }}>{r.EntityType}</Tag>}
        </div>
      ),
    },
    { title: 'Mobile', dataIndex: 'CustomerMobile', key: 'mobile', width: 130, render: (t) => t || '—' },
    { title: 'City', dataIndex: 'City', key: 'city', width: 110, render: (t) => t || '—' },
    {
      title: 'Priority', dataIndex: 'Priority', key: 'priority', width: 100,
      render: (p) => p ? <Tag color={PRIORITY_COLORS[p] || 'default'}>{p}</Tag> : '—',
    },
    {
      title: 'Type', dataIndex: 'FollowupType', key: 'type', width: 150,
      render: (t) => t ? <Tag color={TYPE_COLORS[t] || 'blue'}>{t}</Tag> : '—',
    },
    { title: 'Executive', dataIndex: 'ExecutiveName', key: 'exec', width: 110, render: (t) => t || '—' },
  ];

  const historyColumns = [
    { title: 'Call Result', dataIndex: 'CallStatus', key: 'callstatus', width: 120, render: (t, r) => t ? <span>{t}{r.NotConnectedReason ? <Text type="secondary" style={{ fontSize: 11 }}><br />{r.NotConnectedReason}</Text> : ''}</span> : '—' },
    { title: 'Outcome', dataIndex: 'Outcome', key: 'outcome', width: 130, render: (t) => t || '—' },
    { title: 'Duration', dataIndex: 'Duration', key: 'dur', width: 90, render: (d) => d ? `${d} min` : '—' },
  ];

  const statusColumn = {
    title: 'Status', dataIndex: 'Status', key: 'status', width: 110,
    render: (s) => <Tag color={STATUS_COLORS[s] || 'default'}>{s}</Tag>,
  };

  const actionsColumn = {
    title: 'Actions', key: 'actions', width: 180, fixed: 'right', align: 'center',
    render: (_, r) => (
      <Space size={0}>
        <Tooltip title="Call"><Button type="text" size="small" icon={<PhoneOutlined />} href={`tel:${r.CustomerMobile}`} /></Tooltip>
        <Tooltip title="WhatsApp"><Button type="text" size="small" icon={<WhatsAppOutlined style={{ color: '#25D366' }} />} href={`https://wa.me/${(r.CustomerMobile || '').replace(/[^0-9]/g, '')}`} target="_blank" /></Tooltip>
        <Tooltip title="View / Reschedule"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setDetail(r)} /></Tooltip>
        {r.Status === 'Pending' && <Tooltip title="Complete"><Button type="text" size="small" icon={<CheckOutlined style={{ color: '#52c41a' }} />} onClick={() => handleComplete(r)} /></Tooltip>}
        <Popconfirm title="Delete?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(r)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ),
  };

  const columns = [
    ...baseColumns,
    ...(view === 'history' ? historyColumns : []),
    ...(view === 'history' ? [] : [statusColumn]),
    actionsColumn,
  ];

  // Group upcoming items into Tomorrow / This Week / Next Week / This Month / Later
  const groups = useMemo(() => {
    if (!grouped) return null;
    const now = dayjs();
    const buckets = { Tomorrow: [], 'This Week': [], 'Next Week': [], 'This Month': [], Later: [] };
    for (const r of data) {
      const d = dayjs(r.ScheduledAt);
      if (d.isSame(now.add(1, 'day'), 'day')) buckets.Tomorrow.push(r);
      else if (d.isBefore(now.endOf('week'))) buckets['This Week'].push(r);
      else if (d.isBefore(now.add(1, 'week').endOf('week'))) buckets['Next Week'].push(r);
      else if (d.isBefore(now.endOf('month'))) buckets['This Month'].push(r);
      else buckets.Later.push(r);
    }
    return buckets;
  }, [data, grouped]);

  const toolbar = (
    <Row gutter={[8, 8]} style={{ marginBottom: 16 }} justify="space-between">
      <Col xs={24} md={7}>
        <Input.Search placeholder="Search customer, mobile, city, executive…" allowClear
          enterButton={<SearchOutlined />} onSearch={(v) => { setPage(1); setSearch(v); }}
          onChange={(e) => { if (!e.target.value) { setSearch(''); setPage(1); } }} />
      </Col>
      <Col xs={24} md={17}>
        <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
          <DatePicker placeholder="Date" onChange={(d) => setFilter('date', d ? d.format('YYYY-MM-DD') : null)} />
          <Select allowClear placeholder="Priority" style={{ width: 110 }} onChange={(v) => setFilter('Priority', v)}
            options={(settings.priority_color || []).map(p => ({ value: p.Value, label: p.Label }))} />
          <Select allowClear placeholder="Executive" style={{ width: 130 }} onChange={(v) => setFilter('ExecutiveName', v)}
            options={(settings.executive || []).map(p => ({ value: p.Value, label: p.Label }))} />
          <Select allowClear placeholder="Type" style={{ width: 150 }} onChange={(v) => setFilter('FollowupType', v)}
            options={(settings.followup_type || []).map(p => ({ value: p.Value, label: p.Label }))} />
          <Tooltip title="Export CSV"><Button icon={<DownloadOutlined />} onClick={exportCsv} /></Tooltip>
          <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        </Space>
      </Col>
    </Row>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>{meta.title}</Title>
          <Text type="secondary">{meta.subtitle}</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/calls/new')} style={{ borderRadius: 6 }}>
            New Follow-up
          </Button>
        </Col>
      </Row>

      <Card bordered={false} style={{ borderRadius: 8 }} bodyStyle={{ padding: 16 }}>
        {toolbar}

        {grouped ? (
          <Collapse defaultActiveKey={['Tomorrow', 'This Week']} ghost>
            {Object.entries(groups).map(([label, rows]) => (
              <Collapse.Panel key={label} header={<Space><CalendarOutlined /><Text strong>{label}</Text><Badge count={rows.length} showZero color={BRAND} /></Space>}>
                {rows.length ? (
                  <Table dataSource={rows} columns={columns} rowKey="FollowupID" size="small" bordered
                    pagination={false} scroll={{ x: 1100 }} loading={loading} />
                ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="None" />}
              </Collapse.Panel>
            ))}
          </Collapse>
        ) : (
          <Table
            dataSource={data} columns={columns} rowKey="FollowupID" size="small" bordered
            loading={loading} scroll={{ x: 1200 }}
            onChange={(p) => { setPage(p.current); setPageSize(p.pageSize); }}
            pagination={{
              current: page, pageSize, total, showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
            }}
          />
        )}
      </Card>

      <FollowupDetailDrawer open={!!detail} followup={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
};

export default FollowupList;
